import React, { useState } from "react";
import "./TeamPersonnelModal.css";

export default function TeamPersonnelModal({
    isOpen,
    onClose,
    teams,
    personnel,
    paftalar,
    addNewTeam,
    deleteTeam,
    handleAddMember,
    handleRemoveMemberByName,
    canManageTeams,
}) {
    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const [newTeamName, setNewTeamName] = useState("");
    const [personnelSearchQuery, setPersonnelSearchQuery] = useState("");
    const [showOnlyTeamMembers, setShowOnlyTeamMembers] = useState(false); // ✅ Default to false to see all personnel

    if (!isOpen) return null;

    const visiblePaftalar = paftalar.filter((p) => p.gorunur);
    const isPaftaValid = visiblePaftalar.length === 1;

    // Filter teams for the active pafta
    const activePaftaTeams = isPaftaValid
        ? teams.filter((t) => t.paftaId === visiblePaftalar[0].id)
        : [];

    const handleCreateTeam = () => {
        if (!canManageTeams) {
            alert("Takım oluşturmak için proje yöneticisi yetkisi gerekir.");
            return;
        }
        if (!newTeamName.trim()) return;
        addNewTeam(newTeamName.trim());
        setNewTeamName("");
    };

    const handleDeleteTeam = (e, tId) => {
        e.stopPropagation();
        if (!canManageTeams) {
            alert("Takım silmek için proje yöneticisi yetkisi gerekir.");
            return;
        }
        deleteTeam(tId);
        if (selectedTeamId === tId) {
            setSelectedTeamId(null);
        }
    };

    const selectedTeam = activePaftaTeams.find((t) => t.id === selectedTeamId);
    const teamMembers = selectedTeam ? selectedTeam.members || [] : [];

    // 1. O anki Pafta'daki tüm takımların tüm üyelerini bir Set'te topla
    const allMembersInThisPafta = new Set();
    activePaftaTeams.forEach(t => {
        if (t.members) {
            t.members.forEach(m => allMembersInThisPafta.add(m));
        }
    });

    // Arama ve Filtreleme mantığı
    const filteredPersonnel = personnel.filter((p) => {
        const fullName = p.name || p.username;

        // 1. Arama Metni Filtresi
        if (personnelSearchQuery.trim()) {
            if (!fullName.toLowerCase().includes(personnelSearchQuery.toLowerCase())) {
                return false;
            }
        }

        // 2. Takım Üyeleri / Olmayanlar Filtresi
        if (selectedTeamId) {
            const isMember = teamMembers.includes(fullName);
            const isInAnotherTeamContext = !isMember && allMembersInThisPafta.has(fullName);

            if (showOnlyTeamMembers) {
                // Checkbox işaretliyse, sadece seçili takımın üyelerini göster
                if (!isMember) return false;
            } else {
                // Checkbox işaretli DEĞİLSE, sadece henüz BİR TAKIMA GİRMEMİŞ olanları (ve mevcut takımın üyelerini) göster
                // Yanlış: Mevcut takımın üyeleri "Takım Üyeleri" checkbox'ı dışında da görünür mü?
                // Mantık: Checkbox yokken (Genel listeleme) -> Zaten BİR takıma girmiş adam listelenmemeli, 
                // EĞER o kişi mevcut seçili takımda DEĞİLSE.
                if (isInAnotherTeamContext) return false;

                // Ayrıca, eğer "Sadece Takım Üyeleri" tiki kapalıysa, o takımın halihazırdaki 
                // üyelerini göstermek ister mi? Kullanıcı "Takım Üyelerini Gizle, Sadece boşta olanları göster" de diyebilir.
                // Mevcut kodda, tiki kapattığında "takım üyelerini GİZLİYORDUK" (Sadece diğerlerini gösteriyordu).
                if (isMember) return false;
            }
        }

        return true;
    });

    return (
        <div className="tp-modal-overlay">
            <div className="tp-modal-content">
                <div className="tp-modal-header">
                    <h2>Pafta - Takım ve Üye Yönetimi</h2>
                    <button className="tp-close-btn" onClick={onClose}>
                        ✖
                    </button>
                </div>

                {!isPaftaValid ? (
                    <div className="tp-warning">
                        Takım ve personelleri yönetmek için sadece 1 (bir) adet paftanın görünür olması gerekir.
                    </div>
                ) : (
                    <div className="tp-modal-body">
                        {/* Left Column: Teams */}
                        <div className="tp-teams-col">
                            <h3>Takımlar ({visiblePaftalar[0].isim})</h3>
                            <div className="tp-add-team">
                                <input
                                    type="text"
                                    placeholder="Yeni takım adı..."
                                    value={newTeamName}
                                    onChange={(e) => setNewTeamName(e.target.value)}
                                    disabled={!canManageTeams}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleCreateTeam();
                                    }}
                                />
                                <button onClick={handleCreateTeam} disabled={!canManageTeams}>Oluştur</button>
                            </div>
                            <ul className="tp-team-list">
                                {activePaftaTeams.map((team) => (
                                    <li
                                        key={team.id}
                                        className={`tp-team-item ${selectedTeamId === team.id ? "active" : ""
                                            }`}
                                        onClick={() => setSelectedTeamId(team.id)}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <span className="tp-team-name">{team.name}</span>
                                            <span className="tp-team-count">
                                                {team.members ? team.members.length : 0} Kişi
                                            </span>
                                        </div>
                                        {canManageTeams ? (
                                            <button
                                                className="tp-btn-delete-team"
                                                onClick={(e) => handleDeleteTeam(e, team.id)}
                                                title="Takımı Sil"
                                            >
                                                🗑
                                            </button>
                                        ) : null}
                                    </li>
                                ))}
                                {activePaftaTeams.length === 0 && (
                                    <li className="tp-empty">Henüz takım bulunmuyor</li>
                                )}
                            </ul>
                        </div>

                        {/* Right Column: Personnel */}
                        <div className="tp-personnel-col">
                            <h3>Kayıtlı Personeller</h3>
                            <div className="tp-personnel-filters">
                                <input
                                    type="text"
                                    placeholder="Personel ara..."
                                    value={personnelSearchQuery}
                                    onChange={(e) => setPersonnelSearchQuery(e.target.value)}
                                    className="tp-search-input"
                                />
                                <label className="tp-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={showOnlyTeamMembers}
                                        onChange={(e) => setShowOnlyTeamMembers(e.target.checked)}
                                        disabled={!selectedTeamId} // Takım seçilmemişse filtre manasız
                                    />
                                    Takım Üyeleri
                                </label>
                            </div>
                            <ul className="tp-personnel-list">
                                {filteredPersonnel.map((p) => {
                                    const fullName = p.name || p.username;
                                    const isMember = teamMembers.includes(fullName);

                                    return (
                                        <li key={p.id} className="tp-personnel-item">
                                            <div className="tp-personnel-info">
                                                <span className="tp-personnel-name">{fullName}</span>
                                                <span className="tp-personnel-role">
                                                    {p.role === "admin" ? "Yönetici" : "Personel"}
                                                </span>
                                            </div>
                                            <div className="tp-personnel-actions">
                                                {!selectedTeamId ? (
                                                    <span style={{ fontSize: "0.8rem", color: "#64748b", fontStyle: "italic" }}>
                                                        Takım Seçin
                                                    </span>
                                                ) : !canManageTeams ? (
                                                    <span style={{ fontSize: "0.8rem", color: "#64748b", fontStyle: "italic" }}>
                                                        Sadece yönetici düzenleyebilir
                                                    </span>
                                                ) : isMember ? (
                                                    <button
                                                        className="tp-btn-remove"
                                                        onClick={() =>
                                                            handleRemoveMemberByName(selectedTeamId, fullName)
                                                        }
                                                    >
                                                        Kaldır
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="tp-btn-add"
                                                        onClick={() =>
                                                            handleAddMember(selectedTeamId, fullName)
                                                        }
                                                    >
                                                        Ekle
                                                    </button>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                                {filteredPersonnel.length === 0 && (
                                    <li className="tp-empty">Listelenecek personel bulunamadı.</li>
                                )}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
